import {
	BASE_PRECISION,
	BASE_PRECISION_EXP,
	BigNum,
	MarketStatus,
	PerpMarketAccount,
	PositionDirection,
	PRICE_PRECISION_EXP,
	ZERO,
	MarketType,
	User,
	BN,
	QUOTE_SPOT_MARKET_INDEX,
} from '@drift-labs/sdk';
import {
	ENUM_UTILS,
	UIMarket,
	OpenPosition,
	COMMON_UI_UTILS,
} from '@drift/common';
import { useMemo } from 'react';
import NumLib from 'src/utils/NumLib';
import useOraclePrice from 'src/hooks/useOraclePrice';
import useHighLeverageSpotsOpen from 'src/hooks/useHighLeverageSpotsOpen';
import useMarketMaxLeverage from 'src/hooks/useMarketMaxLeverages';
import useDriftClient from 'src/hooks/useDriftClient';
import { usePinTradeformSizeIfMaxLeverageSelected } from './usePinTradeformSizeIfMaxLeverageSelected';
import { handleTotalMaxTradeSizeCalculation } from '../utils/handleTotalMaxTradeSizeCalculation';
import { useMaxLeverageAvailable } from './useMaxLeverageAvailable';
import useGetDisplayLeverageForMarket from 'src/hooks/useGetDisplayLeverageForMarket';
import { useBalanceRowData } from 'src/hooks/useBalanceRowData';
import useMarketSettingsStore from 'src/stores/useMarketSettingsStore';
import { ITradeForm } from 'src/stores/DriftStore/types/tradeForm';
import { ZERO_PRICE_BIGNUM } from 'src/constants/math';
import { ISOLATED_POSITION_DEPOSIT_BUFFER_DENOMINATOR } from 'src/constants/trade';

const DEFAULT_MAX_BREAKDOWN = {
	newSide: {
		quoteAmount: 0, // oracle based
		baseAmount: 0,
	},
	currentSide: {
		quoteAmount: 0, // oracle based
		baseAmount: 0,
	},
};

export type UseSyncPerpMaxLeverageAndFundsBreakdownTradeData = Pick<
	ITradeForm,
	| 'side'
	| 'orderType'
	| 'isFlippingPosition'
	| 'priceBoxStringValue'
	| 'secondaryPriceBoxStringValue'
>;

interface Params {
	tradeData: UseSyncPerpMaxLeverageAndFundsBreakdownTradeData;
	selectedMarket: UIMarket | null;
	perpMarketAccount: PerpMarketAccount | undefined;
	currentUserClient: User | undefined;
	currentAccountFreeCollateral: BN | undefined;
	currentPosition: OpenPosition | undefined;
	currentPositionDirection: 'long' | 'short';
}

/**
 * Hook that synchronizes maximum leverage and funds breakdown for perpetual trading.
 *
 * This hook manages the complex calculation of maximum available trading amounts for
 * perpetual markets, handling various edge cases including:
 * - Different order types (market, limit, stop orders)
 * - Reduce-only market status
 * - Position flipping scenarios
 * - High leverage mode availability
 *
 * The hook calculates and maintains:
 * - Maximum leverage available for the current market
 * - Maximum base amount available for trading
 * - Maximum quote amount available (adjusted for order type)
 * - Funds breakdown for current and new position sides
 *
 */
export const useSyncPerpMaxLeverageAndFundsBreakdown = ({
	tradeData,
	selectedMarket,
	perpMarketAccount,
	currentUserClient,
	currentPosition,
	currentPositionDirection,
	currentAccountFreeCollateral,
}: Params) => {
	const driftClient = useDriftClient();
	const oraclePrice = useOraclePrice(selectedMarket?.marketId);

	const { highLeverageSpotsOpen } = useHighLeverageSpotsOpen();
	const { hasHighLeverage: isHighLeverageMarket } = useMarketMaxLeverage(
		MarketType.PERP,
		selectedMarket?.marketIndex
	);

	// Calculate display leverage for margin ratio
	const { getMarginModeForMarket } = useMarketSettingsStore();
	const userKey =
		currentUserClient?.getUserAccount().subAccountId +
		'_' +
		currentUserClient?.getUserAccount().authority.toBase58();
	const marginMode = getMarginModeForMarket(selectedMarket?.key, userKey);

	const displayLeverage = useGetDisplayLeverageForMarket(
		selectedMarket?.marketIndex
	);

	const maxMarginRatio =
		COMMON_UI_UTILS.convertLeverageToMarginRatio(displayLeverage);

	const canEnterHighLeverageMode = useMemo(() => {
		return (
			!isNaN(highLeverageSpotsOpen) &&
			highLeverageSpotsOpen > 0 &&
			isHighLeverageMarket
		);
	}, [highLeverageSpotsOpen, isHighLeverageMarket]);

	const freeCollateral = NumLib.formatBn.fromQuote(
		currentAccountFreeCollateral ?? ZERO
	);

	const bufferedMinOrderSize = useMemo(() => {
		return perpMarketAccount
			? BigNum.from(
					perpMarketAccount.amm.minOrderSize,
					BASE_PRECISION_EXP
				).toNum() * 1.01 // add 1% buffer to the min order size to account for oracle price fluctuations
			: 0;
	}, [perpMarketAccount]);

	const {
		side,
		orderType,
		isFlippingPosition,
		priceBoxStringValue,
		secondaryPriceBoxStringValue,
	} = tradeData;

	const maxFundAvailableBreakdown = useMemo(() => {
		if (
			!driftClient ||
			!currentUserClient ||
			!currentUserClient?.isSubscribed ||
			!perpMarketAccount
		) {
			return DEFAULT_MAX_BREAKDOWN;
		}

		const isUserPriceDependentOrderType =
			orderType === 'limit' ||
			orderType === 'stopLimit' ||
			orderType === 'stopMarket' ||
			orderType === 'takeProfitLimit' ||
			orderType === 'takeProfitMarket';

		// if the oracle price is 0, the program allows the user to input infinite base amount, regardless of the order type (even for limit orders)
		// we still want to cap the user's input amount on the UI if possible, in this case limit orders allows for that
		const shouldUseTradeFormPrice =
			oraclePrice?.eqZero() && isUserPriceDependentOrderType;

		const tradeFormPrice = BigNum.fromPrint(
			priceBoxStringValue,
			PRICE_PRECISION_EXP
		);

		const priceToUse = shouldUseTradeFormPrice ? tradeFormPrice : oraclePrice;

		// both oracle price and user input price are 0
		if (!priceToUse || priceToUse.eqZero()) {
			return DEFAULT_MAX_BREAKDOWN;
		}

		const isMarketInReduceOnly = ENUM_UTILS.match(
			perpMarketAccount.status,
			MarketStatus.REDUCE_ONLY
		);

		if (isMarketInReduceOnly) {
			// Handle reduce only case and return early because it's easier to reason about

			if (!currentPosition) {
				// prevent opening a position when market is in reduce only mode
				return DEFAULT_MAX_BREAKDOWN;
			}

			if (!isFlippingPosition) {
				// prevent increasing position size when market is in reduce only mode
				return DEFAULT_MAX_BREAKDOWN;
			}

			// The max size available to reduce is the same as the current position size
			const currentPositionBaseSizeBigNum = BigNum.from(
				currentPosition.baseSize,
				BASE_PRECISION_EXP
			).abs();

			return {
				newSide: {
					quoteAmount: 0,
					baseAmount: 0,
				},
				currentSide: {
					quoteAmount: currentPositionBaseSizeBigNum
						.mul(priceToUse)
						.div(BASE_PRECISION)
						.toNum(),
					baseAmount: currentPositionBaseSizeBigNum.toNum(),
				},
			};
		}

		const maxTradeSizeUsdc = currentUserClient.getMaxTradeSizeUSDCForPerp(
			selectedMarket?.marketIndex ?? 0,
			side === 'buy' ? PositionDirection.LONG : PositionDirection.SHORT,
			undefined,
			canEnterHighLeverageMode,
			maxMarginRatio,
			marginMode
		);

		const finalPriceToUse = priceToUse;
		const currentPriceToUse = priceToUse;

		const newMaxFundAvailableBreakdown = handleTotalMaxTradeSizeCalculation(
			finalPriceToUse,
			currentPriceToUse,
			maxTradeSizeUsdc
		);

		const newMaxBaseAvailable =
			newMaxFundAvailableBreakdown.newSide.baseAmount +
			newMaxFundAvailableBreakdown.currentSide.baseAmount;

		// If the new max base available is less than the minimum order size, set the max trade size to 0
		if (newMaxBaseAvailable < bufferedMinOrderSize) {
			return DEFAULT_MAX_BREAKDOWN;
		}

		return newMaxFundAvailableBreakdown;
	}, [
		driftClient,
		currentPosition,
		side,
		orderType,
		oraclePrice.toNum(),
		priceBoxStringValue,
		currentPositionDirection,
		selectedMarket?.marketIndex,
		isFlippingPosition,
		currentUserClient,
		currentUserClient?.isSubscribed,
		canEnterHighLeverageMode,
		perpMarketAccount,
		bufferedMinOrderSize,
		maxMarginRatio,
		marginMode,
	]);

	const maxBaseAvailable =
		maxFundAvailableBreakdown.newSide.baseAmount +
		maxFundAvailableBreakdown.currentSide.baseAmount;

	// this max quote better reflects the displayed max quote in the UI in accordance with the order type
	const maxQuoteAvailable = useMemo((): number => {
		const oraclePriceBasedMaxQuoteAvailable =
			maxFundAvailableBreakdown.newSide.quoteAmount +
			maxFundAvailableBreakdown.currentSide.quoteAmount;

		if (orderType === 'market') {
			// multiply maxBaseAvailable by Est. Entry Price when entering with max base
			return oraclePriceBasedMaxQuoteAvailable;
		} else {
			const priceToUse = (() => {
				// If we're dealing with oracle limit, we need to calculate the offset.
				if (orderType === 'oracleLimit') {
					if (!oraclePrice) {
						return 0;
					}

					const priceOffset = oraclePrice.add(
						BigNum.fromPrint(priceBoxStringValue, PRICE_PRECISION_EXP)
					);

					return (
						priceOffset.ltZero() ? ZERO_PRICE_BIGNUM : priceOffset
					).toNum();
				}

				// If the order type is a stop limit or take profit limit, we should use the
				// secondary price box value, as that's the price the size is dependent on.
				return orderType === 'stopLimit' || orderType === 'takeProfitLimit'
					? +secondaryPriceBoxStringValue
					: +priceBoxStringValue;
			})();

			// multiply maxBaseAvailable by Limit Price
			const maxDynamicUiQuoteAvailable = priceToUse
				? maxBaseAvailable * priceToUse
				: 0;
			return maxDynamicUiQuoteAvailable;
		}
	}, [
		orderType,
		maxBaseAvailable,
		maxFundAvailableBreakdown,
		priceBoxStringValue,
		secondaryPriceBoxStringValue,
		oraclePrice,
	]);

	const usdcSpotMarketInfo = useBalanceRowData({
		spotMarketIndex: QUOTE_SPOT_MARKET_INDEX,
	});

	const isolatedMarginModeMaxQuoteAvailable = useMemo(() => {
		const quoteTimesLeverage =
			(usdcSpotMarketInfo?.depositsQuote.toNum() ?? 0) * displayLeverage;
		const isolatedMaxQuote = Math.min(quoteTimesLeverage, maxQuoteAvailable);

		const isolatedMaxQuoteWithBuffer =
			(isolatedMaxQuote * ISOLATED_POSITION_DEPOSIT_BUFFER_DENOMINATOR) /
			(ISOLATED_POSITION_DEPOSIT_BUFFER_DENOMINATOR + 1);
		return Math.min(isolatedMaxQuoteWithBuffer, maxQuoteAvailable);
	}, [usdcSpotMarketInfo, displayLeverage, maxQuoteAvailable]);

	const isolatedMarginModeMaxBaseAvailable = useMemo(() => {
		const priceNum = oraclePrice?.toNum() ?? 0;
		if (priceNum <= 0) return 0;
		return isolatedMarginModeMaxQuoteAvailable / priceNum;
	}, [isolatedMarginModeMaxQuoteAvailable, oraclePrice]);

	const finalMaxQuoteAvailable = useMemo(() => {
		return marginMode === 'isolated'
			? isolatedMarginModeMaxQuoteAvailable
			: maxQuoteAvailable;
	}, [marginMode, isolatedMarginModeMaxQuoteAvailable, maxQuoteAvailable]);

	const finalMaxBaseAvailable = useMemo(() => {
		return marginMode === 'isolated'
			? isolatedMarginModeMaxBaseAvailable
			: maxBaseAvailable;
	}, [marginMode, isolatedMarginModeMaxBaseAvailable, maxBaseAvailable]);

	// Maintain max allowed leverage state (uses margin-mode-aware max base)
	const maxLeverageAvailable = useMaxLeverageAvailable({
		selectedMarket,
		currentUserClient,
		maxBaseAvailable: finalMaxBaseAvailable,
		bufferedMinOrderSize,
		freeCollateral,
		canEnterHighLeverageMode,
	});

	usePinTradeformSizeIfMaxLeverageSelected(
		finalMaxQuoteAvailable,
		finalMaxBaseAvailable
	);

	return {
		maxLeverageAvailable,
		maxBaseAvailable: finalMaxBaseAvailable,
		maxQuoteAvailable: finalMaxQuoteAvailable,
	};
};
